import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, CustomProvider } from 'firebase/app-check';
import { 
  getAI, 
  getGenerativeModel, 
  getTemplateGenerativeModel, 
  GoogleAIBackend, 
  type GenerativeModel, 
  type TemplateGenerativeModel 
} from 'firebase/ai';

// ── Firebase Client Config ──────────────────────────────────────────────────
const getFirebaseConfig = () => ({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || import.meta.env.VITE_GEMINI_API_KEY || 'AIzaSyCPbsA64Fo5O33-dKGhka1R6q8ttNWsA18',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'tablegreet.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'tablegreet',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'tablegreet.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1038587057965',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:1038587057965:web:81c84ffe4c8f66523865a4',
});

let appSingleton: FirebaseApp | null = null;
let aiModelSingleton: GenerativeModel | null = null;
let templateModelSingleton: TemplateGenerativeModel | null = null;
let appCheckInitialized = false;

export function initAppCheck(app?: FirebaseApp) {
  if (appCheckInitialized || typeof window === 'undefined') return;
  try {
    const targetApp = app || getApp();
    const config = getFirebaseConfig();
    const debugToken = import.meta.env.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN || '054cbf39-fe4f-42f6-bb4d-8efd6d814e57';

    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;

    // Use CustomProvider to exchange the registered App Check token directly with Firebase App Check REST API
    const customProvider = new CustomProvider({
      getToken: async () => {
        try {
          const endpoint = `https://firebaseappcheck.googleapis.com/v1/projects/${config.projectId}/apps/${config.appId}:exchangeDebugToken?key=${config.apiKey}`;
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ debug_token: debugToken }),
          });
          if (!res.ok) {
            const errText = await res.text();
            console.warn('[Firebase AppCheck] Token exchange failed:', res.status, errText);
            throw new Error(`Token exchange failed: ${res.status}`);
          }
          const data = await res.json();
          const ttlMatch = (data.ttl || '').match(/^([\d.]+)(s)$/);
          const ttlSeconds = ttlMatch ? parseFloat(ttlMatch[1]) : 3600;
          return {
            token: data.token,
            expireTimeMillis: Date.now() + ttlSeconds * 1000,
          };
        } catch (e) {
          console.error('[Firebase AppCheck] CustomProvider error:', e);
          throw e;
        }
      },
    });

    initializeAppCheck(targetApp, {
      provider: customProvider,
      isTokenAutoRefreshEnabled: true,
    });
    appCheckInitialized = true;
  } catch (appCheckError) {
    console.warn('[Firebase] App Check initialization skipped or failed:', appCheckError);
  }
}

function getApp(): FirebaseApp {
  if (!appSingleton) {
    const apps = getApps();
    appSingleton = apps.length > 0 ? apps[0] : initializeApp(getFirebaseConfig());
  }
  return appSingleton;
}

const SYSTEM_INSTRUCTION_TEXT = [
  'You are an expert hospitality and culinary AI translator and data extractor for TableGreet.',
  'You format menu titles and culinary descriptions with mouthwatering precision.',
  'You strictly output valid JSON only without markdown formatting backticks or meta commentary.',
  'Never invent fake allergens or prices unless found directly in provided sources.',
].join(' ');

export function getAIModel(modelName = 'gemini-3.6-flash'): GenerativeModel {
  const app = getApp();
  initAppCheck(app);

  if (aiModelSingleton) return aiModelSingleton;

  const ai = getAI(app, { backend: new GoogleAIBackend() });

  aiModelSingleton = getGenerativeModel(ai, {
    model: modelName,
    systemInstruction: {
      role: 'system',
      parts: [
        {
          text: SYSTEM_INSTRUCTION_TEXT,
        },
      ],
    },
    generationConfig: {
      temperature: 0.2,
      maxOutputTokens: 8192,
      responseMimeType: 'application/json',
    },
  });

  return aiModelSingleton;
}

// ── Template Generative Model Accessor ─────────────────────────────────────
// Returns the singleton TemplateGenerativeModel instance for server-side prompt templates.
// This interface is required when Enforce Template-Only Mode is enabled in Firebase.
export function getTemplateModel(): TemplateGenerativeModel {
  const app = getApp();
  initAppCheck(app);

  if (templateModelSingleton) return templateModelSingleton;

  const ai = getAI(app, { backend: new GoogleAIBackend() });
  templateModelSingleton = getTemplateGenerativeModel(ai);

  return templateModelSingleton;
}

// ── Unified Firebase AI Template Caller ────────────────────────────────────
// Directly executes server-side prompt templates registered in the Firebase Console.
// Variables passed in the dictionary are dynamically populated into the server prompt.
export async function callTemplateAI(params: {
  templateId: string;
  variables: Record<string, unknown>;
}): Promise<string> {
  const { templateId, variables } = params;

  try {
    const templateModel = getTemplateModel();
    const result = await templateModel.generateContent(templateId, variables);
    const text = result.response.text();
    if (!text) {
      throw new Error(`Firebase AI template [${templateId}] returned an empty response.`);
    }
    return text;
  } catch (sdkErr: any) {
    const errMsg = sdkErr?.message || String(sdkErr);
    console.error(`Firebase AI Template [${templateId}] SDK error:`, errMsg);

    if (errMsg.includes('App Check token') || errMsg.includes('401')) {
      throw new Error(
        'AI security verification could not be completed. Please refresh the page and try again.'
      );
    }

    if (
      errMsg.includes('429') ||
      errMsg.toLowerCase().includes('quota') ||
      errMsg.includes('RESOURCE_EXHAUSTED') ||
      errMsg.includes('rate-limit')
    ) {
      const match = errMsg.match(/retry in\s+([0-9.]+s?)/i) || errMsg.match(/retryDelay["']?:\s*["']?([0-9.]+s?)/i);
      let seconds = 0;
      if (match) {
        const parsed = parseFloat(match[1]);
        if (!isNaN(parsed) && parsed > 0) {
          seconds = Math.ceil(parsed);
        }
      }
      const retryMessage = seconds > 0
        ? ` Please try again in about ${seconds} seconds.`
        : ' Please wait a moment and try again.';
      throw new Error(
        `AI is currently experiencing high demand.${retryMessage}`
      );
    }

    throw new Error(`AI service error: ${errMsg || 'temporarily unavailable'}.`);
  }
}

// ── Unified Firebase AI Caller ──
export async function callGeminiAI(params: {
  prompt: string | any[];
  modelName?: string;
}): Promise<string> {
  const modelName = params.modelName || 'gemini-3.6-flash';

  try {
    const model = getAIModel(modelName);
    const result = await model.generateContent(params.prompt);
    const text = result.response.text();
    if (!text) {
      throw new Error('Firebase AI returned an empty response.');
    }
    return text;
  } catch (sdkErr: any) {
    const errMsg = sdkErr?.message || String(sdkErr);
    // Keep raw diagnostic details in console for debugging
    console.error('Firebase AI SDK error:', errMsg);

    if (errMsg.includes('App Check token') || errMsg.includes('401')) {
      throw new Error(
        'AI security verification could not be completed. Please refresh the page and try again.'
      );
    }

    if (
      errMsg.includes('429') ||
      errMsg.toLowerCase().includes('quota') ||
      errMsg.includes('RESOURCE_EXHAUSTED') ||
      errMsg.includes('rate-limit')
    ) {
      const match = errMsg.match(/retry in\s+([0-9.]+s?)/i) || errMsg.match(/retryDelay["']?:\s*["']?([0-9.]+s?)/i);
      let seconds = 0;
      if (match) {
        const parsed = parseFloat(match[1]);
        if (!isNaN(parsed) && parsed > 0) {
          seconds = Math.ceil(parsed);
        }
      }
      const retryMessage = seconds > 0
        ? ` Please try again in about ${seconds} seconds.`
        : ' Please wait a moment and try again.';
      throw new Error(
        `AI is currently experiencing high demand.${retryMessage}`
      );
    }

    throw new Error('AI service is temporarily unavailable. Please try again in a few moments.');
  }
}

// ── Helper to strip JSON fences and extract JSON cleanly ───────────────────
function cleanJsonResponse(rawText: string): string {
  if (!rawText) return '{}';
  let cleaned = rawText.trim();

  // Strip leading/trailing code blocks
  cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();

  // Find outermost curly braces
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace >= firstBrace) {
    cleaned = cleaned.substring(firstBrace, lastBrace + 1);
  }

  return cleaned;
}

// Repairs truncated or incomplete JSON returned by AI when token limits are reached
function repairTruncatedJson(jsonStr: string): string {
  let str = jsonStr.trim();
  if (!str) return '{}';

  // Strip markdown code fences
  str = str.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();

  // Find first opening brace or bracket
  const firstBrace = str.indexOf('{');
  const firstBracket = str.indexOf('[');
  let startIdx = 0;
  if (firstBrace !== -1 && (firstBracket === -1 || firstBrace < firstBracket)) {
    startIdx = firstBrace;
  } else if (firstBracket !== -1) {
    startIdx = firstBracket;
  }
  str = str.substring(startIdx);

  // Scan through the characters to track open brackets/braces and open strings
  const stack: ('{' | '[')[] = [];
  let inString = false;
  let isEscaped = false;
  let lastGoodCutoff = 0;

  for (let i = 0; i < str.length; i++) {
    const char = str[i];

    if (inString) {
      if (isEscaped) {
        isEscaped = false;
      } else if (char === '\\') {
        isEscaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{' || char === '[') {
      stack.push(char);
    } else if (char === '}') {
      if (stack.length > 0 && stack[stack.length - 1] === '{') {
        stack.pop();
        if (stack.length <= 2) {
          lastGoodCutoff = i + 1;
        }
      }
    } else if (char === ']') {
      if (stack.length > 0 && stack[stack.length - 1] === '[') {
        stack.pop();
        if (stack.length <= 2) {
          lastGoodCutoff = i + 1;
        }
      }
    }
  }

  // If stack is empty and not inString, it was already valid or closed
  if (stack.length === 0 && !inString) {
    return str;
  }

  // If it was truncated inside an array of items and we have at least one complete object closed:
  if (lastGoodCutoff > 0) {
    let cleanSlice = str.substring(0, lastGoodCutoff).trim();
    cleanSlice = cleanSlice.replace(/,\s*$/, '');
    const remainingStack: ('{' | '[')[] = [];
    let remInString = false;
    let remEscaped = false;
    for (let i = 0; i < cleanSlice.length; i++) {
      const c = cleanSlice[i];
      if (remInString) {
        if (remEscaped) remEscaped = false;
        else if (c === '\\') remEscaped = true;
        else if (c === '"') remInString = false;
        continue;
      }
      if (c === '"') {
        remInString = true;
      } else if (c === '{' || c === '[') {
        remainingStack.push(c);
      } else if (c === '}' && remainingStack[remainingStack.length - 1] === '{') {
        remainingStack.pop();
      } else if (c === ']' && remainingStack[remainingStack.length - 1] === '[') {
        remainingStack.pop();
      }
    }
    while (remainingStack.length > 0) {
      const open = remainingStack.pop();
      cleanSlice += open === '{' ? '}' : ']';
    }
    return cleanSlice;
  }

  // Fallback for single object or key/value truncation:
  if (inString) {
    str += '"';
  }
  str = str.replace(/:\s*"[^"]*$/, ': ""');
  str = str.replace(/,\s*$/, '');
  str = str.replace(/:\s*$/, ': null');
  str = str.replace(/,\s*([}\]])/g, '$1');

  // Recount what is still open
  const fallbackStack: ('{' | '[')[] = [];
  let fbInString = false;
  let fbEscaped = false;
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (fbInString) {
      if (fbEscaped) fbEscaped = false;
      else if (c === '\\') fbEscaped = true;
      else if (c === '"') fbInString = false;
      continue;
    }
    if (c === '"') {
      fbInString = true;
    } else if (c === '{' || c === '[') {
      fallbackStack.push(c);
    } else if (c === '}' && fallbackStack[fallbackStack.length - 1] === '{') {
      fallbackStack.pop();
    } else if (c === ']' && fallbackStack[fallbackStack.length - 1] === '[') {
      fallbackStack.pop();
    }
  }

  while (fallbackStack.length > 0) {
    const open = fallbackStack.pop();
    str += open === '{' ? '}' : ']';
  }

  return str;
}

function parseJsonSafely<T = any>(rawText: string, fallback: T): T {
  if (!rawText || !rawText.trim()) return fallback;

  // 1. Standard parse on cleaned response
  try {
    const cleaned = cleanJsonResponse(rawText);
    return JSON.parse(cleaned) as T;
  } catch {}

  // 2. Trailing comma sanitization
  try {
    const sanitized = cleanJsonResponse(rawText).replace(/,\s*([}\]])/g, '$1');
    return JSON.parse(sanitized) as T;
  } catch {}

  // 3. Resilient truncated JSON repair
  try {
    const repaired = repairTruncatedJson(rawText);
    const parsed = JSON.parse(repaired) as T;
    console.warn('[FirebaseAI] Repaired truncated JSON response successfully.');
    return parsed;
  } catch (e: any) {
    console.error('[FirebaseAI] Failed to parse JSON response:', rawText, e);
    if (fallback !== undefined && Object.keys(fallback as object).length > 0) {
      return fallback;
    }
    throw new Error(`AI returned an unparseable response format: ${e?.message || 'Invalid JSON'}`);
  }
}

// ── Types ───────────────────────────────────────────────────────────────────
export type ItemTranslationResult = Record<string, { name: string; description: string }>;

export type ExtractedMenuItem = {
  name: string;
  description: string;
  price: number;
  dietary_attributes: string[];
  category_name: string;
  // Detected native translations extracted directly from multilingual menus
  translations?: Record<string, { name?: string; description?: string }>;
};

export type ExtractedMenuResult = {
  categories: string[];
  items: ExtractedMenuItem[];
};

// ── 1. Single Item AI Translation ─────────────────────────────────────────
// Translates menu items across enabled locales using the registered menu-translate prompt template.
export async function translateItemWithAI(params: {
  name: string;
  description?: string;
  targetLangs: { code: string; label: string }[];
  baseLanguage?: string;
}): Promise<ItemTranslationResult> {
  const { name, description = '', targetLangs } = params;
  if (!name.trim() || targetLangs.length === 0) return {};

  const result: ItemTranslationResult = {};

  await Promise.all(
    targetLangs.map(async (lang) => {
      try {
        const rawText = await callTemplateAI({
          templateId: 'menu-translate',
          variables: {
            target_language: lang.label || lang.code,
            original_name: name,
            original_description: description || '',
          },
        });
        const parsed = parseJsonSafely<{ translated_name?: string; translated_description?: string }>(
          rawText,
          {}
        );
        result[lang.code] = {
          name: parsed.translated_name || name,
          description: parsed.translated_description || '',
        };
      } catch (err: any) {
        console.warn(`AI translation failed for language ${lang.code}:`, err);
        result[lang.code] = {
          name,
          description,
        };
      }
    })
  );

  return result;
}

// ── 1b. Single Item AI Description Generator ──────────────────────────────
// Generates mouthwatering dish descriptions using the server-side menu-item-description template.
export async function generateItemDescriptionWithAI(params: {
  name: string;
  category?: string;
  ingredients?: string;
  tone?: 'mouthwatering' | 'gourmet' | 'concise' | 'playful';
}): Promise<string> {
  const { name, category = '', ingredients = '', tone = 'mouthwatering' } = params;
  if (!name.trim()) return '';

  try {
    const rawText = await callTemplateAI({
      templateId: 'menu-item-description',
      variables: {
        name,
        ingredients: ingredients || (category ? `Category: ${category}` : 'Fresh restaurant ingredients'),
        style: tone,
      },
    });
    return cleanJsonResponse(rawText).replace(/^["']|["']$/g, '').trim();
  } catch (err: any) {
    console.error('AI Description generation failed:', err);
    throw new Error(err.message || 'AI Description generation failed. Please try again.');
  }
}

// ── 1c. Single Category AI Translation ────────────────────────────────────
// Translates menu category names using the menu-translate server template.
export async function translateCategoryWithAI(params: {
  name: string;
  targetLangs: { code: string; label: string }[];
}): Promise<Record<string, { name: string }>> {
  const { name, targetLangs } = params;
  if (!name.trim() || targetLangs.length === 0) return {};

  const result: Record<string, { name: string }> = {};

  await Promise.all(
    targetLangs.map(async (lang) => {
      try {
        const rawText = await callTemplateAI({
          templateId: 'menu-translate',
          variables: {
            target_language: lang.label || lang.code,
            original_name: name,
            original_description: 'Menu category name',
          },
        });
        const parsed = parseJsonSafely<{ translated_name?: string }>(rawText, {});
        result[lang.code] = {
          name: parsed.translated_name || name,
        };
      } catch (err) {
        console.warn(`Category translation failed for ${lang.code}:`, err);
        result[lang.code] = { name };
      }
    })
  );

  return result;
}

// ── 1d. Single Department AI Translation ──────────────────────────────────
// Translates venue department names using the menu-translate server template.
export async function translateDepartmentWithAI(params: {
  name: string;
  targetLangs: { code: string; label: string }[];
}): Promise<Record<string, { name: string }>> {
  const { name, targetLangs } = params;
  if (!name.trim() || targetLangs.length === 0) return {};

  const result: Record<string, { name: string }> = {};

  await Promise.all(
    targetLangs.map(async (lang) => {
      try {
        const rawText = await callTemplateAI({
          templateId: 'menu-translate',
          variables: {
            target_language: lang.label || lang.code,
            original_name: name,
            original_description: 'Venue department section name',
          },
        });
        const parsed = parseJsonSafely<{ translated_name?: string }>(rawText, {});
        result[lang.code] = {
          name: parsed.translated_name || name,
        };
      } catch (err) {
        console.warn(`Department translation failed for ${lang.code}:`, err);
        result[lang.code] = { name };
      }
    })
  );

  return result;
}

// ── 1e. Raw Menu Text & OCR Structured Extraction ────────────────────────
// Converts unstructured OCR text or scanned paper menu text into valid JSON categories and items.
export async function extractMenuTextWithAI(rawOcrText: string): Promise<ExtractedMenuResult> {
  if (!rawOcrText?.trim()) {
    return { categories: [], items: [] };
  }

  const rawText = await callTemplateAI({
    templateId: 'menu-ocr-extraction',
    variables: {
      raw_ocr_text: rawOcrText,
    },
  });

  const parsed = parseJsonSafely<{
    currency?: string;
    categories?: Array<{
      name: string;
      items: Array<{
        name: string;
        description?: string;
        price?: number | string;
        modifiers?: string[];
      }>;
    }>;
  }>(rawText, { categories: [] });

  const categories: string[] = [];
  const items: ExtractedMenuItem[] = [];

  if (Array.isArray(parsed.categories)) {
    for (const cat of parsed.categories) {
      const catName = (cat.name || 'General').trim();
      if (catName && !categories.includes(catName)) {
        categories.push(catName);
      }
      if (Array.isArray(cat.items)) {
        for (const itm of cat.items) {
          items.push({
            name: itm.name || 'Untitled Item',
            description: itm.description || '',
            price: typeof itm.price === 'number' ? itm.price : parseFloat(String(itm.price || 0)) || 0,
            dietary_attributes: [],
            category_name: catName,
          });
        }
      }
    }
  }

  return { categories, items };
}

// ── 1e. SEO & Website Metadata AI Translation ─────────────────────────────
// Translates and optimizes search engine title tags and meta descriptions across configured locales using 'seo-translate' template.
export async function translateSeoWithAI(params: {
  seoTitle?: string;
  metaDescription?: string;
  venueName: string;
  cuisineTypes?: string[];
  targetLangs: { code: string; label: string }[];
}): Promise<Record<string, { seo_title: string; meta_description: string }>> {
  const { seoTitle = '', metaDescription = '', venueName, cuisineTypes = [], targetLangs } = params;
  if (targetLangs.length === 0) return {};

  const targetLangsList = targetLangs.map(l => `"${l.code}" (${l.label})`).join(', ');
  const cuisinesStr = cuisineTypes.length > 0 ? cuisineTypes.join(', ') : 'Hospitality / Dining';

  try {
    const rawText = await callTemplateAI({
      templateId: 'seo-translate',
      variables: {
        venue_name: venueName,
        cuisine_types: cuisinesStr,
        source_title: seoTitle || `${venueName} | Digital Menu & Online Orders`,
        source_meta_description: metaDescription || `Experience delicious cuisine at ${venueName}. View our interactive digital menu, specials, and book your table.`,
        target_locales: targetLangsList,
      },
    });
    return parseJsonSafely<Record<string, { seo_title: string; meta_description: string }>>(rawText, {});
  } catch (err: any) {
    console.error('AI SEO Translation failed:', err);
    throw new Error(err.message || 'AI SEO Translation failed. Please try again.');
  }
}

// ── 1f. Venue Info & Bio AI Translation ────────────────────────────────────
// Translates and localizes venue branding name, bio/story description, address, and city using 'venue-info-translate' template.
export async function translateVenueInfoWithAI(params: {
  name: string;
  description?: string;
  address?: string;
  city?: string;
  targetLangs: { code: string; label: string }[];
}): Promise<Record<string, { name?: string; description?: string; address?: string; city?: string }>> {
  const { name, description = '', address = '', city = '', targetLangs } = params;
  if (!name.trim() || targetLangs.length === 0) return {};

  const targetLangsList = targetLangs.map(l => `"${l.code}" (${l.label})`).join(', ');

  try {
    const rawText = await callTemplateAI({
      templateId: 'venue-info-translate',
      variables: {
        name,
        description,
        address,
        city,
        target_locales: targetLangsList,
      },
    });
    return parseJsonSafely<Record<string, { name?: string; description?: string; address?: string; city?: string }>>(rawText, {});
  } catch (err: any) {
    console.error('AI Venue Info Translation failed:', err);
    throw new Error(err.message || 'AI Venue Info Translation failed. Please try again.');
  }
}

// ── 2. Batch Menu AI Translation ─────────────────────────────────────────
// Translates batches of menu items and categories simultaneously using 'batch-menu-translate' template.
export async function translateBatchMenuWithAI(params: {
  items: { id: string; name: string; description?: string }[];
  categories: { id: string; name: string }[];
  targetLangs: { code: string; label: string }[];
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{
  itemTranslations: Record<string, ItemTranslationResult>;
  categoryTranslations: Record<string, Record<string, { name: string }>>;
}> {
  const { items, categories, targetLangs, onProgress } = params;
  const targetLangsList = targetLangs.map(l => `"${l.code}" (${l.label})`).join(', ');

  const itemTranslations: Record<string, ItemTranslationResult> = {};
  const categoryTranslations: Record<string, Record<string, { name: string }>> = {};

  // Chunks of 15 items reduce round-trip requests by up to 80% while staying well within token limits
  const chunkSize = 15;
  const totalSteps = Math.ceil(items.length / chunkSize) + (categories.length > 0 ? 1 : 0);
  let step = 0;

  // A. Translate Categories first
  if (categories.length > 0) {
    step++;
    onProgress?.({ current: step, total: totalSteps, message: `Translating ${categories.length} categories...` });

    try {
      const catResText = await callTemplateAI({
        templateId: 'batch-menu-translate',
        variables: {
          target_locales: targetLangsList,
          payload_type: 'categories',
          payload_json: JSON.stringify(categories.map(c => ({ id: c.id, name: c.name }))),
        },
      });
      const parsedCat = parseJsonSafely<Record<string, Record<string, { name: string }>>>(catResText, {});
      Object.assign(categoryTranslations, parsedCat);
    } catch (e: any) {
      console.error('AI category translation failed:', e);
      throw new Error(e.message || 'AI Category translation failed.');
    }
  }

  // B. Translate Items in optimized chunks
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    step++;
    onProgress?.({
      current: step,
      total: totalSteps,
      message: `Translating items ${i + 1}-${Math.min(i + chunkSize, items.length)} of ${items.length}...`,
    });

    // Omit empty or whitespace-only descriptions to save prompt tokens
    const chunkPayload = chunk.map(item => {
      const trimmedDesc = item.description?.trim();
      return {
        id: item.id,
        name: item.name,
        ...(trimmedDesc ? { description: trimmedDesc } : {}),
      };
    });

    try {
      const itemResText = await callTemplateAI({
        templateId: 'batch-menu-translate',
        variables: {
          target_locales: targetLangsList,
          payload_type: 'items',
          payload_json: JSON.stringify(chunkPayload),
        },
      });
      const parsedChunk = parseJsonSafely<Record<string, ItemTranslationResult>>(itemResText, {});
      Object.assign(itemTranslations, parsedChunk);
    } catch (e: any) {
      console.error(`AI item translation failed for chunk ${i}:`, e);
      throw new Error(e.message || 'AI Item translation failed.');
    }
  }

  return { itemTranslations, categoryTranslations };
}

// ── 3. Extract Menu from PDF or Image with AI ─────────────────────────────────────
export async function extractMenuFromPdfWithAI(
  file: File, 
  options?: {
    targetLanguage?: string;
    defaultLocale?: string;
    enabledLocales?: string[];
  }
): Promise<ExtractedMenuResult> {
  // Convert file to base64
  const arrayBuffer = await file.arrayBuffer();
  const base64Data = btoa(
    new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  );

  let mimeType = file.type;
  if (!mimeType) {
    if (file.name.endsWith('.pdf')) mimeType = 'application/pdf';
    else if (file.name.endsWith('.png')) mimeType = 'image/png';
    else if (file.name.endsWith('.jpg') || file.name.endsWith('.jpeg')) mimeType = 'image/jpeg';
    else if (file.name.endsWith('.webp')) mimeType = 'image/webp';
    else mimeType = 'application/pdf';
  }

  const isImage = mimeType.startsWith('image/');
  const docLabel = isImage ? 'image (photo/scan)' : 'PDF document';
  const targetLang = options?.targetLanguage?.trim();
  const defaultLocale = options?.defaultLocale?.trim() || 'en';
  const enabledLocales = (options?.enabledLocales || []).filter(Boolean);

  // Multilingual intelligence prompt configuration
  const languagePromptSection = `
CRITICAL LANGUAGE EXTRACTION & LOCALIZATION RULES:
Venue Default Language: "${targetLang || defaultLocale}" (default locale code: "${defaultLocale}")
Venue Enabled Locales: ${enabledLocales.length > 0 ? JSON.stringify(enabledLocales) : `["${defaultLocale}"]`}

Examine this ${docLabel} carefully to determine if it is single-language or printed in multiple languages (e.g. side-by-side French and Arabic, bilingual Spanish/English, etc.):

1. SCENARIO A - MULTILINGUAL MENU & MULTILINGUAL VENUE:
If the menu contains multiple languages and the venue supports multiple enabled locales:
- Place the venue default language ("${targetLang || defaultLocale}") into the primary "name" and "description".
- Extract the printed secondary language(s) directly into the "translations" object under their respective locale code (e.g. "translations": { "ar-ma": { "name": "...", "description": "..." } }).
- DO NOT machine-translate or modify the native printed translations: extract them exactly as printed on the menu.
- NEVER concatenate multiple languages into a single field (e.g., NEVER output "Salade / سلطة"). Always keep languages strictly separated into their respective fields.

2. SCENARIO B - MULTILINGUAL MENU & SINGLE-LANGUAGE VENUE:
If the menu contains multiple printed languages, but the venue only has one default language ("${targetLang || defaultLocale}"):
- Extract ONLY the text corresponding to the venue's default language into "name" and "description".
- Completely omit and discard the secondary language text. Do not create composite or slashed titles. "translations" can remain empty {}.

3. SCENARIO C - FOREIGN SINGLE-LANGUAGE MENU:
If the menu is printed entirely in a foreign language that differs from "${targetLang || defaultLocale}":
- Translate all category names, item names, culinary descriptions, and dietary attributes into the venue default language "${targetLang || defaultLocale}".
- Ensure culinary terms sound appetizing and culturally natural. "translations" can remain empty {}.`;

  const prompt = `Analyze this restaurant menu ${docLabel}. Extract all menu categories and items.
${languagePromptSection}

For each item extract:
- name: The primary item title (in ${targetLang || defaultLocale})
- description: Ingredients, preparation, or item details (if present, else empty string)
- price: Numeric price (e.g. 12.50, if no price set 0)
- category_name: The category it belongs to (in ${targetLang || defaultLocale}, e.g., Starters, Mains, Desserts, Beverages)
- dietary_attributes: Array of strings matching applicable tags from: ["vegetarian", "vegan", "spicy", "gluten-free", "nut-free", "dairy-free", "healthy"]
- translations: Optional object mapping locale codes (e.g. "ar-ma", "fr-ma", "en") to { "name": string, "description": string } when extracted from a multilingual menu.

Requirements:
- Extract all visible categories and items from the entire document.
- Keep descriptions clear, concise, and appetizing to ensure complete output.
- Return strictly valid JSON in this structure:
{
  "categories": ["Starters", "Mains", "Drinks"],
  "items": [
    {
      "name": "Margherita Pizza",
      "description": "Tomato sauce, mozzarella, fresh basil",
      "price": 14.0,
      "category_name": "Mains",
      "dietary_attributes": ["vegetarian"],
      "translations": {
        "ar-ma": {
          "name": "بيتزا مارغريتا",
          "description": "صلصة طماطم، جبن موزاريلا، ريحان طازج"
        }
      }
    }
  ]
}`;

  const pdfPrompt = [
    {
      inlineData: {
        mimeType,
        data: base64Data,
      },
    },
    prompt,
  ];

  const pdfResText = await callGeminiAI({ prompt: pdfPrompt });
  try {
    const parsed = parseJsonSafely<ExtractedMenuResult>(pdfResText, { categories: [], items: [] });
    const categories = Array.isArray(parsed.categories) ? [...parsed.categories] : [];
    const items = Array.isArray(parsed.items) ? parsed.items : [];

    // Ensure all unique item category names are included in categories list
    items.forEach(item => {
      if (item.category_name && !categories.includes(item.category_name)) {
        categories.push(item.category_name);
      }
    });

    return {
      categories,
      items,
    };
  } catch (err) {
    console.error('Failed to parse extracted menu JSON:', pdfResText, err);
    throw new Error('AI could not parse the menu layout into structured data. Please try another high quality PDF or image.');
  }
}

export const extractMenuWithAI = extractMenuFromPdfWithAI;

// ── 4. Automatic Dietary & Allergen Detection ─────────────────────────────────

export interface DietaryAllergenDetectionResult {
  dietary_attributes: string[];
  allergens: string[];
  confidence?: number;
  reasoning?: string;
}

/**
 * Deterministic culinary heuristics fallback engine for dietary and allergen detection.
 * Ensures zero downtime and 100% kitchen safety when AI services are unreachable.
 */
export function detectDietaryAndAllergensHeuristic(params: {
  name: string;
  description?: string;
  ingredients?: string;
  category?: string;
}): DietaryAllergenDetectionResult {
  const text = `${params.name} ${params.description || ''} ${params.ingredients || ''} ${params.category || ''}`.toLowerCase();
  
  const dietary: Set<string> = new Set();
  const allergens: Set<string> = new Set();
  const reasons: string[] = [];

  // Meat / Poultry / Seafood checks
  const hasPork = /\b(pork|bacon|ham|prosciutto|pancetta|lard|chorizo|salami|pepperoni)\b/.test(text);
  const hasBeef = /\b(beef|steak|veal|bison)\b/.test(text);
  const hasPoultry = /\b(chicken|duck|turkey|poultry)\b/.test(text);
  const hasFish = /\b(salmon|tuna|cod|bass|halibut|trout|fish|anchovy|sardine|snapper|tilapia|swordfish)\b/.test(text);
  const hasShellfish = /\b(shrimp|prawn|crab|lobster|mussel|clam|oyster|scallop|calamari|squid|octopus|shellfish)\b/.test(text);
  const hasMeat = hasPork || hasBeef || hasPoultry || hasFish || hasShellfish || /\b(lamb|mutton|meat|venison)\b/.test(text);

  // Dairy checks
  const hasDairy = /\b(milk|cheese|cream|butter|parmesan|mozzarella|cheddar|ricotta|brie|gouda|feta|yogurt|ghee|burrata|mascarpone|dairy)\b/.test(text);
  
  // Egg checks
  const hasEgg = /\b(egg|eggs|mayo|mayonnaise|aioli|meringue)\b/.test(text);

  // Gluten / Wheat checks
  const hasGluten = /\b(wheat|flour|pasta|fettuccine|spaghetti|penne|bread|bun|brioche|toast|baguette|pizza|crust|noodle|noodles|batter|barley|rye)\b/.test(text);

  // Nut checks
  const hasPeanut = /\b(peanut|peanuts|peanut butter)\b/.test(text);
  const hasTreeNut = /\b(almond|almonds|walnut|walnuts|cashew|cashews|pistachio|pistachios|hazelnut|hazelnuts|pecan|pecans|macadamia|pine nut|pine nuts)\b/.test(text);

  // Soy checks
  const hasSoy = /\b(soy|soya|tofu|edamame|soy sauce|tamari|miso|tempeh)\b/.test(text);

  // Sesame checks
  const hasSesame = /\b(sesame|tahini|goma)\b/.test(text);

  // Spicy checks
  const hasSpicy = /\b(spicy|chili|chilli|jalapeno|jalapeño|habanero|sriracha|harissa|cayenne|wasabi|chipotle|hot sauce|peppers)\b/.test(text);

  // Healthy checks
  const hasHealthy = /\b(salad|quinoa|acai|kale|superfood|organic|avocado|smoothie|steamed)\b/.test(text);

  // Assign Allergens
  if (hasDairy) {
    allergens.add('Dairy');
    reasons.push('Contains dairy ingredients');
  }
  if (hasGluten) {
    allergens.add('Gluten');
    reasons.push('Contains wheat/gluten ingredients');
  }
  if (hasPeanut) {
    allergens.add('Peanuts');
    reasons.push('Contains peanuts');
  }
  if (hasTreeNut) {
    allergens.add('Nuts');
    reasons.push('Contains tree nuts');
  }
  if (hasShellfish) {
    allergens.add('Shellfish');
    reasons.push('Contains shellfish/crustaceans');
  }
  if (hasFish) {
    allergens.add('Fish');
    reasons.push('Contains finfish');
  }
  if (hasEgg) {
    allergens.add('Eggs');
    reasons.push('Contains eggs');
  }
  if (hasSoy) {
    allergens.add('Soy');
    reasons.push('Contains soy');
  }
  if (hasSesame) {
    allergens.add('Sesame');
    reasons.push('Contains sesame');
  }

  // Assign Dietary Attributes
  if (hasSpicy) {
    dietary.add('spicy');
  }
  if (hasHealthy) {
    dietary.add('healthy');
  }
  if (!hasMeat && !hasDairy && !hasEgg && !hasFish && !hasShellfish) {
    dietary.add('vegan');
    dietary.add('vegetarian');
  } else if (!hasMeat && !hasFish && !hasShellfish) {
    dietary.add('vegetarian');
  }
  if (!hasPork && !/\b(alcohol|wine|beer|liquor|bourbon|rum|vodka)\b/.test(text)) {
    dietary.add('halal');
  }
  if (!hasGluten) {
    dietary.add('gluten-free');
  }
  if (!hasDairy) {
    dietary.add('dairy-free');
  }
  if (!hasPeanut && !hasTreeNut) {
    dietary.add('nut-free');
  }

  return {
    dietary_attributes: Array.from(dietary),
    allergens: Array.from(allergens),
    confidence: 0.85,
    reasoning: reasons.join('; ') || 'Heuristic ingredient analysis'
  };
}

/**
 * Analyzes a dish and its ingredients via the server prompt template 'allergen-detection'
 * to automatically detect dietary markers and allergens, with fallback to safety heuristics.
 */
export async function detectDietaryAndAllergensWithAI(params: {
  name: string;
  description?: string;
  ingredients?: string;
  category?: string;
}): Promise<DietaryAllergenDetectionResult> {
  const { name, description = '', ingredients = '', category = '' } = params;
  if (!name.trim()) {
    return { dietary_attributes: [], allergens: [] };
  }

  const combinedIngredients = [
    ingredients,
    description ? `Description: ${description}` : '',
    category ? `Category: ${category}` : '',
  ].filter(Boolean).join('. ');

  try {
    const rawText = await callTemplateAI({
      templateId: 'allergen-detection',
      variables: {
        dish_name: name,
        ingredients: combinedIngredients || name,
      },
    });

    const parsed = parseJsonSafely<{
      allergens?: string[];
      dietary_tags?: string[];
    }>(rawText, {
      allergens: [],
      dietary_tags: [],
    });

    const dietary_attributes = Array.isArray(parsed.dietary_tags) ? parsed.dietary_tags : [];
    const allergens = Array.isArray(parsed.allergens) ? parsed.allergens : [];

    if (dietary_attributes.length === 0 && allergens.length === 0) {
      // Run fallback heuristic if template returned empty
      return detectDietaryAndAllergensHeuristic(params);
    }

    return {
      dietary_attributes,
      allergens,
      confidence: 0.95,
      reasoning: `Identified based on culinary ingredients in ${name}.`,
    };
  } catch (err) {
    console.warn('AI Dietary & Allergen detection failed, executing heuristic safety engine:', err);
    return detectDietaryAndAllergensHeuristic(params);
  }
}

/**
 * Iteratively analyzes and auto-tags a collection of menu items with progress updates.
 */
export async function batchAutoTagMenuItemsWithAI(params: {
  items: { id: string; name: string; description?: string; ingredients?: string; category?: string }[];
  onProgress?: (progress: { current: number; total: number; itemName: string }) => void;
}): Promise<Record<string, DietaryAllergenDetectionResult>> {
  const { items, onProgress } = params;
  const results: Record<string, DietaryAllergenDetectionResult> = {};

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    onProgress?.({
      current: i + 1,
      total: items.length,
      itemName: item.name
    });

    try {
      const detected = await detectDietaryAndAllergensWithAI(item);
      results[item.id] = detected;
    } catch (e) {
      console.error(`Auto-tagging failed for item ${item.name}:`, e);
      results[item.id] = detectDietaryAndAllergensHeuristic(item);
    }
  }

  return results;
}

// ── 7. Marketing & Promotion Assistant ─────────────────────────────────────

export interface MarketingPromotionIdea {
  title: string;
  announcementText: string;
  announcementType: 'info' | 'success' | 'warning' | 'promo';
  promoLabel: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  startTime: string;
  endTime: string;
  days: string[];
  strategyNote: string;
  suggestedTargetType: 'all' | 'categories' | 'items';
}

export interface MarketingAssistantResult {
  campaignIdeas: MarketingPromotionIdea[];
  strategicSummary: string;
}

// Fallback generator ensures the marketing assistant remains operational even if AI provider is temporarily unreachable
function getFallbackMarketingIdeas(venueName: string, goal: string): MarketingAssistantResult {
  const isWeekend = goal === 'weekend_rush';
  const isLunch = goal === 'lunch_crowd';

  return {
    strategicSummary: `Tailored promotional campaign concepts to increase guest frequency and average check size at ${venueName}.`,
    campaignIdeas: [
      {
        title: isLunch ? 'Executive Lunch Perk' : 'Afternoon Happy Hour',
        announcementText: isLunch 
          ? `🍽️ Quick Lunch at ${venueName}: Enjoy 15% off all lunch entrees between 12 PM and 3 PM!`
          : `⚡ Happy Hour at ${venueName}: Enjoy 20% off all beverages and appetizers between 3 PM and 6 PM!`,
        announcementType: 'promo',
        promoLabel: isLunch ? '15% Lunch Entrees' : '20% Off Happy Hour',
        discountType: 'percentage',
        discountValue: isLunch ? 15 : 20,
        startTime: isLunch ? '12:00' : '15:00',
        endTime: isLunch ? '15:00' : '18:00',
        days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        suggestedTargetType: 'all',
        strategyNote: 'Fills traditionally slow seating windows and drives incremental high-margin sales without sacrificing peak yields.',
      },
      {
        title: isWeekend ? 'Weekend Chef Showcase' : 'Midweek Dining Incentive',
        announcementText: isWeekend
          ? `🎉 Weekend Dining at ${venueName}: Savor our signature specials with exclusive table discounts all weekend!`
          : `✨ Midweek Treat at ${venueName}: Special 15% dining reward on all signature dishes Monday through Thursday.`,
        announcementType: 'success',
        promoLabel: isWeekend ? '10% Weekend Perk' : '15% Midweek Special',
        discountType: 'percentage',
        discountValue: isWeekend ? 10 : 15,
        startTime: '18:00',
        endTime: '22:00',
        days: isWeekend ? ['fri', 'sat', 'sun'] : ['mon', 'tue', 'wed', 'thu'],
        suggestedTargetType: 'all',
        strategyNote: 'Incentivizes advance table reservations and increases multi-course ticket sizes.',
      },
      {
        title: 'Seasonal Tasting Discovery',
        announcementText: `🌟 Limited Time Special: Explore freshly crafted favorites with special promotional pricing this week!`,
        announcementType: 'promo',
        promoLabel: 'Special Tasting Offer',
        discountType: 'percentage',
        discountValue: 10,
        startTime: '13:00',
        endTime: '21:00',
        days: ['wed', 'thu', 'fri', 'sat'],
        suggestedTargetType: 'all',
        strategyNote: 'Generates buzz for new culinary offerings and encourages guest social shares.',
      },
    ],
  };
}

// Generates strategic promotional concepts, announcement banners, and scheduled discounts using 'marketing-promotion-generator' template.
export async function generateMarketingPromotionWithAI(params: {
  venueName: string;
  cuisineType?: string;
  goal?: 'boost_off_peak' | 'weekend_rush' | 'lunch_crowd' | 'seasonal_special' | 'general_buzz';
  tone?: 'festive' | 'sophisticated' | 'urgent' | 'friendly';
  activeCategories?: string[];
  activeItems?: { name: string; category?: string; price?: number }[];
}): Promise<MarketingAssistantResult> {
  const {
    venueName,
    cuisineType = 'Hospitality / Dining',
    goal = 'boost_off_peak',
    tone = 'festive',
    activeCategories = [],
    activeItems = [],
  } = params;

  const goalDescriptions: Record<string, string> = {
    boost_off_peak: 'Increase foot traffic and orders during slow afternoon hours (e.g. 2:00 PM - 6:00 PM weekdays)',
    weekend_rush: 'Maximize revenue and premium basket size during Friday-Sunday evening peaks',
    lunch_crowd: 'Drive quick-service office and daytime dining with bundled lunch incentives',
    seasonal_special: 'Create excitement around limited-time specials, holiday celebrations, or seasonal ingredients',
    general_buzz: 'Generate customer engagement, word of mouth, and first-time visitor conversions',
  };

  const sampleItemsText = activeItems.slice(0, 10).map(i => `${i.name} (${i.category || 'General'})`).join(', ');
  const categoriesText = activeCategories.slice(0, 8).join(', ');

  try {
    const rawText = await callTemplateAI({
      templateId: 'marketing-promotion-generator',
      variables: {
        venue_name: venueName,
        cuisine_type: cuisineType,
        goal: goalDescriptions[goal] || goal,
        tone: tone,
        categories: categoriesText || 'General Menu',
        featured_items: sampleItemsText || 'Signature Dishes',
      },
    });
    const parsed = parseJsonSafely<MarketingAssistantResult>(rawText, getFallbackMarketingIdeas(venueName, goal));

    if (!Array.isArray(parsed.campaignIdeas) || parsed.campaignIdeas.length === 0) {
      return getFallbackMarketingIdeas(venueName, goal);
    }

    return parsed;
  } catch (err: any) {
    console.warn('AI Marketing Promotion generation failed, returning strategic fallback ideas:', err);
    return getFallbackMarketingIdeas(venueName, goal);
  }
}



